import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import Brightness5OutlinedIcon from '@mui/icons-material/Brightness5Outlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

export type PunchKind = 'check_in' | 'lunch_out' | 'lunch_in' | 'check_out';

interface PunchStepCardProps {
  kind: PunchKind;
  /** Short period name, e.g. "Entrada". */
  title: string;
  /** Formatted time (e.g. "08:03") when this step is done, else null. */
  time?: string | null;
  step: number;
  done: boolean;
  /** This is the next actionable step. */
  next: boolean;
  disabled: boolean;
  loading?: boolean;
  tooltip?: string;
  onClick: () => void;
}

const ICONS: Record<PunchKind, React.ReactNode> = {
  check_in: <LoginOutlinedIcon />,
  lunch_out: <RestaurantOutlinedIcon />,
  lunch_in: <Brightness5OutlinedIcon />,
  check_out: <LogoutOutlinedIcon />,
};

/**
 * A single punch-clock period tile (Entrada / Almoço / Volta / Saída).
 * States: `done` (green + check + time), `next` (highlighted, primary,
 * actionable), and idle/locked (muted). Purpose-built — not a generic button.
 */
const PunchStepCard: React.FC<PunchStepCardProps> = ({
  kind,
  title,
  time,
  step,
  done,
  next,
  disabled,
  loading = false,
  tooltip,
  onClick,
}) => {
  const clickable = !disabled && !done;

  const border = done
    ? 'success.main'
    : next
      ? 'primary.main'
      : 'divider';
  const bg = next ? 'primary.main' : done ? 'transparent' : 'transparent';
  const fg = next ? 'primary.contrastText' : done ? 'success.main' : 'text.primary';

  const badgeBg = next
    ? 'rgba(255,255,255,0.18)'
    : done
      ? 'success.main'
      : 'action.hover';
  const badgeFg = next ? 'primary.contrastText' : done ? '#fff' : 'text.secondary';

  const card = (
    <Box
      role="button"
      aria-current={next ? 'step' : undefined}
      aria-disabled={!clickable}
      tabIndex={clickable ? 0 : -1}
      onClick={() => clickable && onClick()}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.5,
        border: '1.5px solid',
        borderColor: border,
        borderRadius: 1,
        bgcolor: bg,
        color: fg,
        cursor: clickable ? 'pointer' : 'default',
        opacity: !clickable && !done && !next ? 0.55 : 1,
        transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
        '&:hover': clickable ? { borderColor: 'primary.main', transform: 'translateY(-1px)' } : undefined,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1,
          bgcolor: badgeBg,
          color: badgeFg,
          '& svg': { fontSize: 22 },
        }}
      >
        {loading ? (
          <CircularProgress size={18} sx={{ color: badgeFg }} />
        ) : done ? (
          <CheckRoundedIcon />
        ) : (
          ICONS[kind]
        )}
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            opacity: 0.75,
            lineHeight: 1.2,
          }}
        >
          {step}º · {title}
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontWeight: 800,
            fontSize: '0.95rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? 'Registrando…' : done ? time || 'Registrado' : next ? 'Registrar agora' : time || '—'}
        </Typography>
      </Box>
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow disableInteractive>
      <Box sx={{ height: '100%' }}>{card}</Box>
    </Tooltip>
  ) : (
    card
  );
};

export default PunchStepCard;
